import { createReducer } from '@reduxjs/toolkit';
import { TEntityRef, TAct, TProject } from '@ws/shared/types';
import { getSha1Hash } from '@ws/shared/utils';
import CyrillicToTranslit from 'cyrillic-to-translit-js';

import { IAction } from '../../types/redux';
import { expandArrayWithItemsAtPosition } from '../../utils/array';
import { removeFromArrayIfItemExists } from '../../utils/array/removeFromArrayIfItemExists';
import {
  CHILD_MOVE_TYPE,
  COLLECTION_CREATE,
  COLLECTION_UPDATE_CHILD,
  ICollectionChildUpdateAct,
  ICollectionCreateAct,
  INodeMetaUpdateAct,
  INodeParentUpdateAct,
  INodeRemoveAct,
  INoteContentUpdateAct,
  INoteCreateAct,
  IProjectMetaUpdateAct,
  NODE_META_UPDATE,
  NODE_PARENT_UPDATE,
  NODE_REMOVE,
  NOTE_CREATE,
  NOTE_UPDATE_CONTENT,
  PROJECT_META_UPDATE,
} from '../../utils/events/act.types';
import { ILayoutRestoreAction, LAYOUT_RESTORE } from '../layout';

import {
  IEditorState,
  IEventsApplyAction,
  IInnerApplyRemoteAction,
  INNER_APPLY_EVENTS,
  INNER_APPLY_REMOTE,
  INodeSelectAction,
  IProjectSetAction,
  ISecondaryNodeSelectAction,
  PROJECT_CLEAN,
  PROJECT_SET,
  SELECT_NODE,
  SELECT_SECONDARY_NODE,
} from './editor.types';

const INITIAL_STATE: IEditorState = {
  project: null,
  selected: null,
  secondarySelected: null,
};

// Применение acts одного события к проекту. Общая точка для INNER_APPLY_EVENTS
// (одно событие) и INNER_APPLY_REMOTE (батч из delta) — поведение идентично.
function applyEventActs(project: TProject, payload: IEventsApplyAction): void {
  const acts = payload?.acts;
  if (!acts) {
    return;
  }

  const additionalTime = `${Date.now()}`;
  const transactionUpdateTime = (payload?.time as string) || additionalTime;
  project.updatedAt = transactionUpdateTime;

  for (let i = 0; i < acts.length; i += 1) {
    const currentAct = acts[i];

    switch (currentAct.type) {
      case PROJECT_META_UPDATE: {
        const { payload: actPayload } = currentAct as TAct<IProjectMetaUpdateAct>;
        if (!actPayload) {
          break;
        }

        project.meta = { ...project.meta, ...actPayload.meta };

        break;
      }
      case NODE_PARENT_UPDATE: {
        const { payload: actPayload } = currentAct as TAct<INodeParentUpdateAct>;
        if (!actPayload) {
          break;
        }

        const { ref, parentId } = actPayload;

        if (ref.type === 'note') {
          const oldNote = project.notes[ref.id];
          oldNote.updatedAt = transactionUpdateTime;
          oldNote.parentId = parentId;
          break;
        }

        if (ref.type === 'collection') {
          const oldCollection = project.collections[ref.id];
          oldCollection.updatedAt = transactionUpdateTime;
          oldCollection.parentId = parentId;
        }

        break;
      }
      case NODE_META_UPDATE: {
        const { payload: actPayload } = currentAct as TAct<INodeMetaUpdateAct>;
        if (!actPayload) {
          break;
        }

        const { ref, meta } = actPayload;

        if (ref.type === 'note') {
          const oldNote = project.notes[ref.id];
          oldNote.updatedAt = transactionUpdateTime;
          oldNote.meta = { ...oldNote.meta, ...meta };
          break;
        }

        if (ref.type === 'collection') {
          const oldCollection = project.collections[ref.id];
          oldCollection.updatedAt = transactionUpdateTime;
          oldCollection.meta = { ...oldCollection.meta, ...meta };
        }

        if (ref.id === project.root && meta.name) {
          project.meta.name = meta.name;

          const trans = new CyrillicToTranslit({ preset: 'ru' });
          project.meta.slug = trans.transform(meta.name);
        }

        break;
      }
      case NOTE_UPDATE_CONTENT: {
        const { payload: actPayload } = currentAct as TAct<INoteContentUpdateAct>;
        if (
          !actPayload ||
          typeof actPayload.entityId === 'undefined' ||
          typeof actPayload.content === 'undefined'
        ) {
          break;
        }

        const noteToUpdate = project.notes[actPayload.entityId];
        if (!noteToUpdate) {
          throw new Error('No note to update content');
        }

        noteToUpdate.content = actPayload.content;
        noteToUpdate.hash = actPayload.hash;
        noteToUpdate.updatedAt = transactionUpdateTime;

        project.notes[actPayload.entityId] = noteToUpdate;

        break;
      }
      case COLLECTION_UPDATE_CHILD: {
        const { payload: actPayload } = currentAct as TAct<ICollectionChildUpdateAct>;
        if (
          !actPayload ||
          typeof actPayload.entityId === 'undefined' ||
          typeof actPayload.child === 'undefined'
        ) {
          break;
        }

        const collectionToUpdate = project.collections[actPayload.entityId];
        if (!collectionToUpdate) {
          throw new Error(`No collection with id ${actPayload.entityId} to update a child`);
        }

        let updatedChildren: TEntityRef[] = [...collectionToUpdate.children];

        switch (actPayload.move) {
          case CHILD_MOVE_TYPE.ADD: {
            updatedChildren = removeFromArrayIfItemExists(updatedChildren, actPayload.child);
            updatedChildren = expandArrayWithItemsAtPosition(
              updatedChildren,
              [actPayload.child],
              actPayload.position,
            );
            break;
          }
          case CHILD_MOVE_TYPE.REMOVE: {
            updatedChildren = removeFromArrayIfItemExists(updatedChildren, actPayload.child);
            break;
          }
          default:
            throw new Error(`No such move type ${actPayload.move} to update collection`);
        }

        collectionToUpdate.children = updatedChildren;
        collectionToUpdate.hash = getSha1Hash(JSON.stringify(updatedChildren));
        collectionToUpdate.updatedAt = transactionUpdateTime;

        project.collections[actPayload.entityId] = collectionToUpdate;

        break;
      }
      case NOTE_CREATE: {
        const { payload: actPayload } = currentAct as TAct<INoteCreateAct>;
        if (!actPayload || !actPayload.note) {
          break;
        }

        actPayload.note.updatedAt = transactionUpdateTime;
        project.notes[actPayload.note.id] = actPayload.note;

        break;
      }
      case COLLECTION_CREATE: {
        const { payload: actPayload } = currentAct as TAct<ICollectionCreateAct>;
        if (!actPayload || !actPayload.collection) {
          break;
        }

        actPayload.collection.updatedAt = transactionUpdateTime;
        project.collections[actPayload.collection.id] = actPayload.collection;

        break;
      }
      case NODE_REMOVE: {
        const { payload: actPayload } = currentAct as TAct<INodeRemoveAct>;
        if (!actPayload || !actPayload.ref) {
          break;
        }

        const { ref } = actPayload;

        if (ref.type === 'note') {
          delete project.notes[ref.id];
          break;
        } else if (ref.type === 'collection') {
          delete project.collections[ref.id];
        }

        break;
      }
      default:
        console.error(`Unknown event with type ${currentAct.type}`);
    }
  }
}

export const editorReducer = createReducer(INITIAL_STATE, (builder) => {
  builder
    .addCase(PROJECT_SET, (state: IEditorState, action: IAction<IProjectSetAction>) => {
      state.project = action.payload?.project || null;
    })
    .addCase(PROJECT_CLEAN, (state: IEditorState) => {
      state.project = null;
      state.selected = null;
    })
    .addCase(SELECT_NODE, (state: IEditorState, action: IAction<INodeSelectAction>) => {
      state.selected = action.payload?.selected || null;
    })
    .addCase(
      SELECT_SECONDARY_NODE,
      (state: IEditorState, action: IAction<ISecondaryNodeSelectAction>) => {
        state.secondarySelected = action.payload?.secondarySelected || null;
      },
    )
    .addCase(LAYOUT_RESTORE, (state: IEditorState, action: IAction<ILayoutRestoreAction>) => {
      if (!action.payload?.layout) {
        return;
      }

      // Только сплит. selected НЕ трогаем: его единственный писатель — роутинг,
      // а сохранённый selected нужен лишь чтобы построить URL при входе в проект.
      state.secondarySelected = action.payload.layout.secondarySelected;
    })
    .addCase(
      INNER_APPLY_EVENTS,
      ({ project }: IEditorState, action: IAction<IEventsApplyAction>) => {
        console.log('INNER_APPLY_EVENTS');
        if (!project || !action.payload) {
          return;
        }

        applyEventActs(project, action.payload);
      },
    )
    .addCase(
      INNER_APPLY_REMOTE,
      ({ project }: IEditorState, action: IAction<IInnerApplyRemoteAction>) => {
        console.log('INNER_APPLY_REMOTE');
        if (!project || !action.payload) {
          return;
        }

        // Session fence: применяем батч только своему проекту. Токен несёт
        // generation, но reducer знает лишь projectId активного проекта — это
        // вторая линия защиты; первая (generation) проверена в saga через
        // isCurrent() непосредственно перед put.
        if (action.payload.session.projectId !== project.id) {
          console.warn('INNER_APPLY_REMOTE: session projectId mismatch — batch dropped');
          return;
        }

        for (let i = 0; i < action.payload.events.length; i += 1) {
          applyEventActs(project, action.payload.events[i]);
        }
      },
    );
});
